import { ComponentRef, DestroyRef, inject, Injectable } from '@angular/core';
import { FV_CHIP_COLORS } from '@discocil/fv-components';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  defer,
  EMPTY,
  finalize,
  first,
  forkJoin,
  fromEventPattern,
  map,
  merge,
  Observable,
  of,
  switchMap,
  tap,
  timeout,
} from 'rxjs';
import { IInfoWidgetBaseConfig } from 'src/app/pages/service/pages/account-page/interface/info-widget.interface';
import { InfoWidgetSelectorModalComponent } from 'src/app/shared/components/modals/info-widget-selector-modal/info-widget-selector-modal.component';
import { IInfoWidgetSelectorConfig } from 'src/app/shared/interface/info-widget-selector.interface';
import { GlobalDynamicFvModalLoaderService } from 'src/app/shared/services/global-dynamic-fv-modal-loader/global-dynamic-fv-modal-loader.service';
import { GlobalSnackbarService } from 'src/app/shared/services/global-snackbar/global-snackbar.service';
import { BLUETOOTH_EVENT } from '../../enums/bluetooth-event.enum';
import { IBluetoothDevice } from '../../interfaces/bluetooth-device.interface';
import { Logger } from '../../interfaces/Logger';
import { BluetoothStorageService } from './bluetooth-storage.service';
import { noBluetoothDevices } from '../../config/bluetooth-device-snackbar.config';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CAPACITOR_SERVICE } from '../../../shared/capacitor-service/token/capacitor-service.token';

@Injectable({
  providedIn: 'root',
})
export class BluetoothManagerService {
  private readonly capacitorService = inject(CAPACITOR_SERVICE);
  private readonly globalDynamicFvModalLoaderService = inject(
    GlobalDynamicFvModalLoaderService
  );
  private readonly globalSnackbarService = inject(GlobalSnackbarService);
  private readonly logger = inject(Logger);
  private readonly bluetoothStorage = inject(BluetoothStorageService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  translations: Record<string, string> = {};

  constructor() {
    this.getTranslations();
    this.loadDeviceOnInit();
  }

  loadPrinterModal(): Observable<boolean | null> {
    let options: IInfoWidgetBaseConfig[] = [];

    return this.scanDevices().pipe(
      first(),
      timeout(3000),
      catchError((error) => {
        this.logger.error(
          new Error(
            `[BluetoothManager][loadPrinterModal] Error scanning devices`
          ),
          error
        );

        return of(null);
      }),
      switchMap((devices) =>
        forkJoin({
          devices: of(devices),
          currentPrinter: this.bluetoothStorage.getCurrentPrinter(),
        })
      ),
      switchMap(({ devices, currentPrinter }) => {
        if (!devices) {
          this.globalSnackbarService.open(noBluetoothDevices);

          return EMPTY;
        }

        devices.forEach((device: IBluetoothDevice) => {
          const infoWidget: IInfoWidgetBaseConfig =
            this.formatDeviceAsInfoWidget(device, currentPrinter);

          options.push(infoWidget);
        });

        options = options.sort(
          (firstOption, secondOption) =>
            (firstOption.order ?? 0) - (secondOption.order ?? 0)
        );

        const modalRef = this.globalDynamicFvModalLoaderService.openModal(
          InfoWidgetSelectorModalComponent,
          {
            config: {
              title: this.translations['global.printers'],
              options,
              closeOnSelectOption: true,
              showSubmitButton: false,
            } as IInfoWidgetSelectorConfig<IInfoWidgetBaseConfig>,
          }
        );

        return this.listenInfoWidgetSelectorSubmit(modalRef, devices);
      })
    );
  }

  removeCurrentPrinter(): void {
    this.bluetoothStorage.removeCurrentPrinter();
  }

  private scanDevices(): Observable<IBluetoothDevice[]> {
    return defer(() => {
      const devices$ = this.handleDeviceScanningResponse();

      this.capacitorService.postMessage(BLUETOOTH_EVENT.scanDevices);

      return devices$;
    }).pipe(
      first(),
      finalize(() => {
        this.capacitorService.removeListener(BLUETOOTH_EVENT.scanDevices);
      })
    );
  }

  private connectDevice(device: IBluetoothDevice): Observable<boolean> {
    return defer(() => {
      const device$ = this.handleDeviceConnectionResponse();

      this.capacitorService.postMessage(BLUETOOTH_EVENT.connectDevice, device);

      return device$;
    }).pipe(
      first(),
      finalize(() => {
        this.capacitorService.removeListener(BLUETOOTH_EVENT.connectDevice);
      })
    );
  }

  private listenInfoWidgetSelectorSubmit(
    modalRef: ComponentRef<InfoWidgetSelectorModalComponent>,
    devices: IBluetoothDevice[]
  ): Observable<boolean | null> {
    return modalRef.instance.submitAction.pipe(
      first(),
      switchMap((optionSelected) => {
        if (optionSelected) {
          const selectedDevice = devices?.find(
            (device) => device.address === optionSelected?.id
          );

          if (selectedDevice) {
            const connectDevice$ = this.connectDevice(selectedDevice).pipe(
              first(),
              timeout(3000),
              catchError((error) => {
                this.logger.error(
                  new Error(
                    `[BluetoothManager][listenInfoWidgetSelectorSubmit] Error connection devices`
                  ),
                  error
                );

                return of(false);
              }),
              switchMap((isConnected: boolean) => {
                if (isConnected) {
                  return this.bluetoothStorage
                    .insertCurrentPrinterIntoIndexedDB(selectedDevice)
                    .pipe(
                      first(),
                      map(() => isConnected)
                    );
                }

                return of(isConnected);
              })
            );

            return connectDevice$;
          }

          return of(null);
        }

        return of(null);
      })
    );
  }

  private formatDeviceAsInfoWidget(
    device: IBluetoothDevice,
    currentPrinter: IBluetoothDevice | null
  ): IInfoWidgetBaseConfig {
    const isCurrentPrinter = device.address === currentPrinter?.address;

    return {
      id: device.address,
      chips: [
        {
          title: isCurrentPrinter
            ? `${this.translations['global.connected']}`
            : `${this.translations['global.not_connected']}`,
          color: isCurrentPrinter ? FV_CHIP_COLORS.green : FV_CHIP_COLORS.amber,
        },
      ],
      order: isCurrentPrinter ? 0 : 99,
      title: device.name,
      background: { color: 'zinc', weight: '900' },
      isDisabled: false,
      showCheckbox: false,
      showBorder: false,
    };
  }

  private handleDeviceConnectionResponse(): Observable<boolean> {
    const responseConnectionSuccess$ = fromEventPattern<boolean>((handler) =>
      this.capacitorService.addListener(
        BLUETOOTH_EVENT.responseConnectDeviceSuccess,
        handler
      )
    );

    const responseConnectionFailed$ = fromEventPattern<boolean>((handler) =>
      this.capacitorService.addListener(
        BLUETOOTH_EVENT.responseConnectDeviceFailed,
        handler
      )
    );

    return merge(responseConnectionSuccess$, responseConnectionFailed$).pipe(
      first(),
      finalize(() => {
        this.capacitorService.removeListener(
          BLUETOOTH_EVENT.responseConnectDeviceSuccess
        );
        this.capacitorService.removeListener(
          BLUETOOTH_EVENT.responseConnectDeviceFailed
        );
      })
    );
  }

  private handleDeviceScanningResponse(): Observable<IBluetoothDevice[]> {
    const responseDevices$ = fromEventPattern<IBluetoothDevice[]>((handler) =>
      this.capacitorService.addListener(
        BLUETOOTH_EVENT.responseDevices,
        handler
      )
    );

    return responseDevices$.pipe(
      first(),
      finalize(() => {
        this.capacitorService.removeListener(BLUETOOTH_EVENT.responseDevices);
      })
    );
  }

  private getTranslations(): void {
    this.translateService
      .get(['global.printers', 'global.connected', 'global.not_connected'])
      .pipe(
        first(),
        tap((translations: { [key: string]: string }) => {
          this.translations = translations;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private loadDeviceOnInit(): void {
    forkJoin({
      devices: this.scanDevices(),
      currentPrinter: this.bluetoothStorage.getCurrentPrinter(),
    })
      .pipe(
        first(),
        timeout({ each: 10000, with: () => of(null) }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response) => {
        if (response) {
          const { devices, currentPrinter } = response;

          if (devices && currentPrinter) {
            const device = devices.find(
              (device) => device.address === currentPrinter.address
            );

            if (device) {
              this.connectDevice(device);
            }
          }
        }
      });
  }
}
